import { z } from 'zod';
import { TFunction } from '../_hooks/useAddProductSchema';

// Color item schema with optional dynamic image list (from Antd UploadFile)
const colorImageFileSchema = z.object({
  uid: z.string(),
  name: z.string(),
  url: z.string().optional(),
  originFileObj: z.custom<File>().optional(),
  status: z.string().optional(),
  uploading: z.boolean().optional(),
});

const getColorItemSchema = (t: TFunction) =>
  z.object({
    color: z.object(
      {
        id: z.union([z.string(), z.null()]).optional(),
        name: z.string().trim().optional(),
      },
      {
        invalid_type_error: t('requiredField'),
      }
    ).optional(),
    image: z.array(colorImageFileSchema).optional(),
  });

export const getSkuInventorySchema = (t: TFunction) =>
  z.object({
    productType: z.enum(['cart', 'rfq']).default('cart'),
    applyVariants: z.boolean().default(false),
    material: z.string().optional(),
    storage: z.string().optional(),
    colorAttributeId: z.string().optional(),
    colors: z.array(getColorItemSchema(t)).optional(),
    applyPricingTiers: z.boolean().default(false),

    // Legacy compatibility fields if any other part of codebase references them
    hasVariants: z.boolean().default(false),
    numAttributes: z.preprocess(
      (val) =>
        val === '' || val === undefined || val === null
          ? undefined
          : Number(val),
      z
        .number()
        .int()
        .min(1, { message: t('minLength', { value: 1 }) })
        .max(5, { message: t('maxLength', { value: 5 }) })
        .optional()
    ),
    attributeNames: z
      .array(z.string().min(1, { message: t('requiredField') }))
      .optional(),
    attributeValues: z.array(z.array(z.string())).optional(),
    attributes: z.record(z.string(), z.array(z.string()).optional()).optional(),
    variants: z
      .array(
        z.object({
          _displayDetails: z.array(
            z.object({
              attrId: z.string(),
              detail: z.object({
                id: z.union([z.string(), z.null()]),
                name: z.string(),
                nameEn: z.string(),
                nameAr: z.string(),
              }),
            })
          ).optional(),
          _permutationKey: z.string().optional(),
          sku: z.string().nullable().optional(),
          stock: z.preprocess(
            (val) =>
              val === '' || val === undefined || val === null
                ? undefined
                : Number(val),
            z
              .number()
              .int()
              .nonnegative({ message: t('requiredField') }).optional()
          ),
          price: z.preprocess(
            (val) =>
              val === '' || val === undefined || val === null
                ? undefined
                : Number(val),
            z.number().nonnegative({ message: t('requiredField') }).optional()
          ).optional(),
          priceConverted: z.preprocess(
            (val) =>
              val === '' || val === undefined || val === null
                ? undefined
                : Number(val),
            z.number().nonnegative({ message: t('requiredField') }).optional()
          ).optional(),
        })
      )
      .optional(),
    baseStock: z.preprocess(
      (val) => (val === '' || val === undefined || val === null ? undefined : Number(val)),
      z.number().int().positive({ message: t('requiredField') }).optional()
    ),
    basePrice: z.preprocess(
      (val) => (val === '' || val === undefined || val === null ? undefined : Number(val)),
      z.number().positive({ message: t('requiredField') }).optional()
    ),
  }).superRefine((data, ctx) => {
    // 3. New SKU & Inventory validation rules
    if (data.applyVariants) {
      // Safely extract the list of selected colors
      const colorNames = (data.colors || [])
        .map((c) => c.color?.name?.trim().toLowerCase())
        .filter(Boolean) as string[];

      // If they added color rows AND the category actually has a color attribute, validate them
      if (data.colorAttributeId && data.colors && data.colors.length > 0) {
        data.colors.forEach((colorItem, index) => {
          const name = colorItem.color?.name?.trim().toLowerCase();

          // Check if the current name appears more than once in the safe colorNames array
          if (name && colorNames.filter((n) => n === name).length > 1) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              path: ['colors', index, 'color'],
              message: t('duplicateColor'),
            });
          }

          if (!colorItem.color?.name?.trim()) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              path: ['colors', index, 'color'],
              message: t('requiredField'),
            });
          }
          if (!colorItem.image || colorItem.image.length === 0) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              path: ['colors', index, 'image'],
              message: t('uploadImageForColor'),
            });
          } else if (colorItem.image?.some((img) => img.uploading)) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              path: ['colors', index, 'image'],
              message: t('uploadInProgress'),
            });
          }
        });
      }

      const isTiersApplied = data.productType === 'rfq' && data.applyPricingTiers;

      if (!data.variants || data.variants.length === 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['variants'],
          message: t('requiredField'),
        });
      } else {
        data.variants.forEach((variant, index) => {
          // SKU is backend-generated — never validate it on the frontend.
          if (variant.stock === undefined || variant.stock === null || variant.stock < 0) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              path: ['variants', index, 'stock'],
              message: t('requiredField'),
            });
          }

          if (isTiersApplied) {
            // Not allowed to have variant price with tiers. 
            // We just ignore the leftover form state, as the payload builder strips it out anyway.
          } else {
            // Variant price is REQUIRED and must be > 0
            if (variant.price === undefined || variant.price === null || variant.price <= 0) {
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['variants', index, 'price'],
                message: t('requiredField'),
              });
            }
          }
        });
      }
    } else {
      // applyVariants is false
      const isTiersApplied = data.productType === 'rfq' && data.applyPricingTiers;

      // baseStock is always required when not using variants
      if (data.baseStock === undefined || data.baseStock === null || data.baseStock <= 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['baseStock'],
          message: t('requiredField'),
        });
      }

      // basePrice is required unless tiers are applied
      if (!isTiersApplied) {
        if (data.basePrice === undefined || data.basePrice === null || data.basePrice <= 0) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['basePrice'],
            message: t('requiredField'),
          });
        }
      }
    }
  });


